import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import logo1 from "../assets/logo1.png";
import FreeTrialModal from "./FreeTrialModal";
import FaqSection from "./FaqSection";
import "animate.css";
import AOS from "aos";
import "aos/dist/aos.css";

const HomePage = () => {
  const [showTrial, setShowTrial] = useState(false);

  useEffect(() => {
    AOS.init({ duration: 1000, once: true });
  }, []);

  return (
    <div>
      {/* SEO + Schema */}
      <Helmet>
        {/* 🔥 Fully Optimized Title */}
        <title>
          AI Recruiter & AI Recruitment Tool | Resume Screening – Screenify
        </title>

        {/* 🔥 Strong Meta Description for Higher CTR */}
        <meta
          name="description"
          content="Screenify is an AI-powered recruitment platform for resume screening, candidate evaluation, document verification, and structured onboarding workflows."
        />

        {/* 🔥 Updated SEO Keywords */}
        <meta
          name="keywords"
          content="AI recruitment tool, hiring automation software, AI resume screening, Document verification, automated hiring, candidate scoring AI, HR tech platform, onboarding automation"
        />

        {/* 🔥 Canonical URL */}
        <link rel="canonical" href="https://theairecruiter.ai/" />

        {/* 🔥 OpenGraph / Social Cards */}
        <meta
          property="og:title"
          content="Screenify – AI Hiring Automation Tool"
        />
        <meta
          property="og:description"
          content="AI-powered resume screening, Document verification, candidate scoring, and hiring automation."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://theairecruiter.ai/" />
        <meta
          property="og:image"
          content="https://theairecruiter.ai/og-image.jpg"
        />

        {/* 🔥 Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Screenify – AI Hiring Automation Tool"
        />
        <meta
          name="twitter:description"
          content="Automated resume screening, BGV, and onboarding powered by AI."
        />
        <meta
          name="twitter:image"
          content="https://theairecruiter.ai/og-image.jpg"
        />

        {/* 🔥 Main Website Schema */}
        <script type="application/ld+json">
          {`
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "name": "Screenify",
      "url": "https://theairecruiter.ai",
      "potentialAction": {
        "@type": "SearchAction",
        "target": "https://theairecruiter.ai/?q={search_term_string}",
        "query-input": "required name=search_term_string"
      }
    }
    `}
        </script>

        {/* 🔥 Software Application Schema (Boosts Ranking HIGHLY) */}
        <script type="application/ld+json">
          {`
    {
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      "name": "Screenify",
      "operatingSystem": "Web",
      "applicationCategory": "HRSoftware",
      "description": "Screenify is an AI recruitment and hiring automation tool offering resume screening, Document verification, candidate scoring, and onboarding workflows.",
      "url": "https://theairecruiter.ai",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "INR"
      }
    }
    `}
        </script>

        {/* 🔥 FAQ Schema */}
        <script type="application/ld+json">
          {`
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": [
        {
          "@type": "Question",
          "name": "What does your Document verification include?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Our Document verification includes employment checks, education verification, criminal record checks, identity validation, and more."
          }
        },
        {
          "@type": "Question",
          "name": "How does AI give the validation reason?",
          "acceptedAnswer": {
            "@type": "Answer",
            "text": "Screenify provides human-like validation reasons using AI, such as: candidate experience match, technical skills, education alignment, and JD relevance."
          }
        }
      ]
    }
    `}
        </script>
      </Helmet>

      {/* Navbar */}
      <nav className="navbar navbar-expand-lg navbar-light bg-white shadow-sm sticky-top">
        <div className="container">
          <Link className="navbar-brand mx-auto" to="/home">
            <img src={logo1} alt="logo" style={{ height: "50px" }} />
            <span className="web-name ms-2">Screenify</span>
          </Link>
          <button
            className="navbar-toggler"
            type="button"
            data-bs-toggle="collapse"
            data-bs-target="#navbarNav"
            aria-controls="navbarNav"
            aria-expanded="false"
            aria-label="Toggle navigation"
          >
            <span className="navbar-toggler-icon"></span>
          </button>
          <div
            className="collapse navbar-collapse justify-content-between"
            id="navbarNav"
          >
            <ul className="navbar-nav mx-auto">
              <li className="nav-item">
                <Link className="nav-link active fw-semibold" to="/">
                  Home
                </Link>
              </li>
              <li className="nav-item">
                <Link className="nav-link" to="/ai-recruiter">
                  AI Recruiter
                </Link>
              </li>

              <li className="nav-item">
                <Link className="nav-link" to="/about">
                  About us
                </Link>
              </li>
              <li className="nav-item">
                <Link className="nav-link" to="/contact">
                  Contact
                </Link>
              </li>
              <li className="nav-item">
                <Link className="nav-link" to="/blog">
                  Blog
                </Link>
              </li>
              {/* <li className="nav-item">
                <button
                  className="btn btn-warning"
                  onClick={() => setShowTrial(true)}
                >
                  Free Trial
                </button>
                <FreeTrialModal
                  show={showTrial}
                  onClose={() => setShowTrial(false)}
                />
              </li> */}
            </ul>
            <ul className="navbar-nav ms-lg-auto mt-3 mt-lg-0 text-center">
              <li className="nav-item mb-2 mb-lg-0 me-lg-2">
                <button
                  className="btn btn-warning w-100 w-lg-auto"
                  onClick={() => setShowTrial(true)}
                >
                  Free Trial
                </button>
                <FreeTrialModal
                  show={showTrial}
                  onClose={() => setShowTrial(false)}
                />
              </li>

              <li className="nav-item mb-2 mb-lg-0 me-lg-2">
                <Link
                  className="btn btn-outline-primary w-100 w-lg-auto"
                  to="/login"
                >
                  Login
                </Link>
              </li>

              <li className="nav-item">
                <Link
                  className="btn btn-outline-primary w-100 w-lg-auto"
                  to="/signup"
                >
                  Sign Up
                </Link>
              </li>
            </ul>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section
        className="hero text-center text-white"
        style={{
          background: "linear-gradient(135deg, #007bff, #00c6ff)",
          padding: "100px 0",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <div className="container" data-aos="fade-up">
          <h1 className="display-4 fw-bold animate__animated animate__fadeInDown">
            AI Recruiter for Smarter Hiring Decisions
          </h1>

          <p className="lead mt-3 animate__animated animate__fadeInUp">
            Screenify is an AI-powered recruitment platform for resume
            screening, candidate evaluation, document verification, and
            structured onboarding workflows.
          </p>
          <div className="mt-4 animate__animated animate__fadeInUp">
            <button
              className="btn btn-light btn-lg shadow "
              onClick={() => setShowTrial(true)}
            >
              Start Free Trial
            </button>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section id="how-it-works" className="py-5 bg-light">
        <div className="container">
          <h2 className="text-center mb-5" data-aos="fade-up">
            How It Works
          </h2>
          <div className="row g-4">
            {[
              {
                icon: "fas fa-upload",
                title: "Upload Resumes",
                desc: "Start by uploading the candidates' resumes.",
              },
              {
                icon: "fas fa-file-alt",
                title: "Select Job Description",
                desc: "Define the job role requirements for AI analysis.",
              },
              {
                icon: "fas fa-chart-line",
                title: "AI Analyzes & Scores",
                desc: "Let the AI review, analyze, and score each candidate.",
              },
              {
                icon: "fas fa-check",
                title: "Shortlist & Manage",
                desc: "Easily shortlist and manage top candidates.",
              },
            ].map((item, i) => (
              <div
                key={i}
                className="col-md-3 text-center"
                data-aos="fade-up"
                data-aos-delay={i * 200}
              >
                <div className="p-4 rounded-4 shadow-sm bg-white h-100 hover-scale">
                  <i className={`${item.icon} fa-3x text-primary mb-3`}></i>
                  <h5 className="fw-bold">{item.title}</h5>
                  <p className="mb-0">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-5" style={{ background: "#f8f9fa" }}>
        <div className="container">
          <h2 className="text-center mb-5" data-aos="fade-up">
            Why Choose Screenify?
          </h2>
          <div className="row g-4">
            {[
              {
                icon: "fas fa-clock",
                title: "Save Time",
                desc: "Automated resume parsing saves hours of manual work.",
              },
              {
                icon: "fas fa-brain",
                title: "Improve Accuracy",
                desc: "AI-driven analysis ensures precise candidate shortlisting.",
              },
              {
                icon: "fas fa-balance-scale",
                title: "Reduce Bias",
                desc: "Objective scoring creates fair hiring processes.",
              },
            ].map((item, i) => (
              <div
                key={i}
                className="col-md-4 text-center"
                data-aos="fade-up"
                data-aos-delay={i * 200}
              >
                <div className="p-4 rounded-4 shadow-sm bg-white hover-scale">
                  <i className={`${item.icon} fa-3x text-primary mb-3`}></i>
                  <h5 className="fw-bold">{item.title}</h5>
                  <p>{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <FaqSection />

      {/* Contact Section */}
      <section className="py-5 bg-dark">
        <div className="container text-center text-white">
          <h2
            className="fw-bold display-5"
            data-aos="fade-up"
            data-aos-delay="300"
          >
            Get in Touch
          </h2>
          <p class="mb-4" data-aos="fade-up" data-aos-delay="300">
            We’d love to hear from you! Reach out with any questions or
            feedback.
          </p>

          <div
            className="row g-4 justify-content-center"
            style={{ borderBottom: "2px solid rgba(255, 255, 255, 0.3)" }}
          >
            {[
              {
                title: "Call Us",
                icon: "fas fa-phone-alt",
                lines: ["+91 96115 12281", "+91 90083 09255"],
                color: "text-primary",
              },
              {
                title: "Email",
                icon: "fas fa-envelope",
                lines: ["contactus@archipeltechnologies.com"],
                color: "text-warning",
              },
              {
                title: "Follow Us",
                icon: "fas fa-share-alt",
                social: true,
              },
            ].map((item, i) => (
              <div
                className="col-md-4 mb-3"
                key={i}
                data-aos="fade-up"
                data-aos-delay={i * 200}
              >
                <div
                  className="p-4 rounded-4 shadow text-center hover-scale"
                  style={{
                    background: "rgba(255, 255, 255, 0.1)",
                    backdropFilter: "blur(10px)",
                  }}
                >
                  <i className={`${item.icon} fa-3x mb-3 ${item.color}`}></i>
                  <h5 className="fw-bold mb-3">{item.title}</h5>

                  {item.social ? (
                    <div>
                      <a href="#" className="me-3">
                        <i className="fab fa-linkedin fa-2x text-primary"></i>
                      </a>
                      <a href="#" className="me-3">
                        <i
                          className="fab fa-instagram fa-2x"
                          style={{ color: "#E4405F" }}
                        ></i>
                      </a>
                      <a href="#">
                        <i className="fab fa-youtube fa-2x text-danger"></i>
                      </a>
                    </div>
                  ) : (
                    item.lines.map((line, idx) => (
                      <p key={idx} className="mb-1">
                        {item.icon === "fas fa-envelope" ? (
                          <a
                            href={`mailto:${line}`}
                            className="text-decoration-none text-white"
                          >
                            <i className={`${item.icon} me-2 text-warning`}></i>
                            {line}
                          </a>
                        ) : (
                          <a
                            href={`tel:${line}`}
                            className="text-decoration-none text-white"
                          >
                            <i className={`${item.icon} me-2 text-primary`}></i>
                            {line}
                          </a>
                        )}
                      </p>
                    ))
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer
        className="bg-gradient text-dark py-3"
        style={{ background: "linear-gradient(90deg, #007bff, #00c6ff)" }}
      >
        <div className="container d-flex flex-column flex-lg-row justify-content-between align-items-center text-center">
          <p className="mb-0">
            © Copyright {new Date().getFullYear()} Archipel Technlogies Pvt Ltd.
            . All Rights Reserved.
          </p>
          <div>
            <Link className="text-dark me-3" to="/privacy-policy">
              Privacy & Cookies
            </Link>
            <Link className="text-dark" to="/terms-and-conditions">
              Terms of Use
            </Link>
          </div>
        </div>
      </footer>

      {/* Extra CSS */}
      <style>{`
        .hover-scale:hover {
          transform: translateY(-6px);
          transition: 0.3s;
        }
      `}</style>
    </div>
  );
};

export default HomePage;
